import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import { PrimaryBtn } from "components/common/button";
import {
  PlaceInfoTable,
  FacilityInfoTable,
  BurialPlotsTable,
  ImgInfoTable,
} from "components/burial/detail";
import { OneBtnModal, TwoBtnModal } from "components/common/modal";
import type { IBurialDetail } from "service/interface/burial";
import { delPark, putPark } from "service/api/burial";
import routerConstant from "service/constant/routerConstant";
import responseCodeConstant from "service/constant/responseCodeConstant";
import { useBtnDisabled } from "shared/hooks";

export function BurialDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false);
  const [isDeleteSuccessModalVisible, setIsDeleteSuccessModalVisible] =
    useState<boolean>(false);
  const [isSaveSuccessModalVisible, setIsSaveSuccessModalVisible] =
    useState<boolean>(false);
  const [detailData, setDetailData] = useState<IBurialDetail>({
    ...({} as IBurialDetail),
    isPublic: true,
    BurialPlots: [],
    SearchOptions: [],
    Images: [],
  });
  const isBtnDisable = useBtnDisabled(detailData, [
    "name",
    "operatingHours",
    "telephone",
    "address",
  ]);
  const { data } = useSWR(`/admin/manage/park/${params.id}`);

  useEffect(() => {
    if (data?.data.id) {
      let BurialPlotsArr: IBurialDetail["BurialPlots"] = [];
      data.data.BurialPlots?.forEach((plots: any) => {
        plots.BurialPlotTypes?.forEach((types: any) => {
          types.BurialPlotTypeDetails?.forEach((detail: any) => {
            BurialPlotsArr = [
              ...BurialPlotsArr,
              {
                id: plots.id,
                name: plots.name,
                nameNum: plots.nameNum,
                BurialPlotTypes: [
                  {
                    ...types,
                    BurialPlotTypeDetails: [detail],
                  },
                ],
              },
            ];
          });
        });
      });
      let copiedData = data.data;
      delete copiedData.createdAt;
      delete copiedData.latitude;
      delete copiedData.location;
      delete copiedData.longitude;

      setDetailData({
        ...copiedData,
        SearchOptions: data.data.SearchOptions.map((option: any) => option.id),
        BurialPlots: BurialPlotsArr,
      });
    }
  }, [data]);

  const handleSaveBtn = async () => {
    const saveRes = await putPark({
      parkId: detailData.id,
      body: detailData,
    });
    if (saveRes.status === responseCodeConstant.SUCCESS.ok) {
      setIsSaveSuccessModalVisible(true);
    }
  };

  const handleDeleteBtn = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteModalVisible = async () => {
    const delRes = await delPark(detailData.id + "");
    if (delRes.status === responseCodeConstant.SUCCESS.ok) {
      setIsDeleteModalVisible(false);
      setIsDeleteSuccessModalVisible(true);
    }
  };

  const handleSuccessBtn = () => {
    navigate(routerConstant.BURIAL.list);
  };

  return (
    <>
      <OneBtnModal
        isVisible={isDeleteSuccessModalVisible}
        setIsVisible={setIsDeleteSuccessModalVisible}
        title="삭제 완료"
        subTitle="성공적으로 삭제되었습니다."
        btnClick={handleSuccessBtn}
        btnLabel="확인"
      />
      <OneBtnModal
        isVisible={isSaveSuccessModalVisible}
        setIsVisible={setIsSaveSuccessModalVisible}
        title="저장 완료"
        subTitle="수정이 완료되었습니다."
        btnLabel="확인"
        btnClick={handleSuccessBtn}
      />
      <TwoBtnModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title="삭제 확인"
        subTitle="장지 정보를 삭제하시겠습니까?"
        btnLabel="삭제"
        btnClick={handleDeleteModalVisible}
      />
      <div className="h-full min-h-[100vh] w-[calc(100%-273px)] bg-[#fdfdfd]">
        <div className="mx-auto w-[1167px] p-[78px_80px_72px_64px]">
          <p className="m-0 mb-[8px] text-[16px] font-semibold leading-[20px] text-gray-4 opacity-[0.5]">
            메모리올 관리자 화면
          </p>
          <div className="flex items-center justify-between">
            <p className="m-0 text-[32px] font-medium leading-[40px] text-black">
              장지 정보
            </p>
            <div className="flex items-center gap-[12px]">
              <PrimaryBtn
                color="white"
                label="삭제하기"
                onClick={handleDeleteBtn}
              />
              <PrimaryBtn
                isDisabled={
                  isBtnDisable ||
                  typeof detailData.religion !== "number" ||
                  // detailData.SearchOptions?.length === 0 ||
                  detailData.BurialPlots?.length === 0
                }
                label="저장하기"
                onClick={handleSaveBtn}
              />
            </div>
          </div>
          <div className="mt-[32px] rounded-[12px] bg-white p-[48px_40px_64px_48px] shadow-[0px_0px_8px_rgba(0,0,0,0.1)]">
            <PlaceInfoTable data={detailData} setData={setDetailData} />
            <FacilityInfoTable data={detailData} setData={setDetailData} />
            <BurialPlotsTable data={detailData} setData={setDetailData} />
            <ImgInfoTable data={detailData} setData={setDetailData} />
          </div>
        </div>
      </div>
    </>
  );
}
